/**
 * CORE MESSAGING CONSTANTS
 * 
 * VitaCore's core positioning: "personal transformation system based on discipline"
 * NOT a fitness app - a discipline-building system
 */

import { SupportedLanguage } from '../types';

export const CORE_MESSAGES: Record<SupportedLanguage, {
  coreMessage: string;
  differentiator: string;
  promiseOptions: {
    days7: string;
    days14: string;
    days30: string;
  };
}> = {
  en: {
    coreMessage: "VitaCore doesn't give you plans. It turns you into a disciplined person.",
    differentiator: "VitaCore is not motivation. It is structure.",
    promiseOptions: {
      days7: "7 days to build the habit",
      days14: "14 days to notice the change",
      days30: "30 days to a better version of you"
    }
  },
  es: {
    coreMessage: "VitaCore no te da planes. Te convierte en una persona disciplinada.",
    differentiator: "VitaCore no es motivación. Es estructura.",
    promiseOptions: {
      days7: "7 días para crear el hábito",
      days14: "14 días para notar el cambio",
      days30: "30 días para una versión mejor de ti"
    }
  },
  it: {
    coreMessage: "VitaCore non ti dà piani. Ti trasforma in una persona disciplinata.",
    differentiator: "VitaCore non è motivazione. È struttura.",
    promiseOptions: {
      days7: "7 giorni per creare l'abitudine",
      days14: "14 giorni per notare il cambiamento",
      days30: "30 giorni per una versione migliore di te"
    }
  },
  fr: {
    coreMessage: "VitaCore ne vous donne pas de plans. Il fait de vous une personne disciplinée.",
    differentiator: "VitaCore n'est pas la motivation. C'est la structure.",
    promiseOptions: {
      days7: "7 jours pour créer l'habitude",
      days14: "14 jours pour remarquer le changement",
      days30: "30 jours pour une meilleure version de vous"
    }
  },
  pt: {
    coreMessage: "VitaCore não te dá planos. Transforma-te numa pessoa disciplinada.",
    differentiator: "VitaCore não é motivação. É estrutura.",
    promiseOptions: {
      days7: "7 dias para criar o hábito",
      days14: "14 dias para notar a mudança",
      days30: "30 dias para uma versão melhor de ti"
    }
  },
  de: {
    coreMessage: "VitaCore gibt dir keine Pläne. Es macht dich zu einer disziplinierten Person.",
    differentiator: "VitaCore ist keine Motivation. Es ist Struktur.",
    promiseOptions: {
      days7: "7 Tage, um die Gewohnheit zu schaffen",
      days14: "14 Tage, um die Veränderung zu bemerken",
      days30: "30 Tage zu einer besseren Version von dir"
    }
  },
  nl: {
    coreMessage: "VitaCore geeft je geen plannen. Het maakt je tot een gedisciplineerd persoon.",
    differentiator: "VitaCore is geen motivatie. Het is structuur.",
    promiseOptions: {
      days7: "7 dagen om de gewoonte te creëren",
      days14: "14 dagen om de verandering op te merken",
      days30: "30 dagen naar een betere versie van jezelf"
    }
  },
  pl: {
    coreMessage: "VitaCore nie daje ci planów. Przekształca cię w zdyscyplinowaną osobę.",
    differentiator: "VitaCore to nie motywacja. To struktura.",
    promiseOptions: {
      days7: "7 dni, aby stworzyć nawyk",
      days14: "14 dni, aby zauważyć zmianę",
      days30: "30 dni do lepszej wersji siebie"
    }
  },
  ru: {
    coreMessage: "VitaCore не дает вам планов. Он превращает вас в дисциплинированного человека.",
    differentiator: "VitaCore - это не мотивация. Это структура.",
    promiseOptions: {
      days7: "7 дней, чтобы создать привычку",
      days14: "14 дней, чтобы заметить изменение",
      days30: "30 дней к лучшей версии себя"
    }
  }
};

/**
 * Get core message for current language
 */
export const getCoreMessage = (lang: SupportedLanguage): string => {
  return CORE_MESSAGES[lang]?.coreMessage || CORE_MESSAGES.en.coreMessage;
};

/**
 * Get differentiator message for current language
 */
export const getDifferentiator = (lang: SupportedLanguage): string => {
  return CORE_MESSAGES[lang]?.differentiator || CORE_MESSAGES.en.differentiator;
};

/**
 * Get promise text for selected days
 */
export const getPromiseText = (lang: SupportedLanguage, days: 7 | 14 | 30): string => {
  const key = `days${days}` as keyof typeof CORE_MESSAGES.en.promiseOptions;
  return CORE_MESSAGES[lang]?.promiseOptions[key] || CORE_MESSAGES.en.promiseOptions[key];
};

